import { Injectable } from '@angular/core';
import { from, type Observable } from 'rxjs';
import type {
  AdminOfferGetDto,
  AdminOfferListDto,
  AdminOfferPostPutDto,
} from '../../../../backend/main';
import {
  createOfferAsync,
  deleteAdminOfferAsync,
  findAdminOfferAsync,
  listAdminOffersAsync,
  updateOfferAsync,
} from '../../../../backend/main';

import type { GetAllRequestParameters, ListPaginatedDTO } from '@pct/ng-core-dto';
import { UNKNOWN_ITEM } from '@pct/ng-core-dto';
import type { FilteredParams } from '../../../common/request/filtered-params';
import { mapSort } from '../../../common/request/map-sort';
import { convertPaginationParams } from '../../../common/utils/table/convert-pagination-params';
import type { OffersFilter } from './offers-filter';
import type { DtoService } from '../../../common/details-page/dto-service';

@Injectable()
export class OffersDtoService implements DtoService<
  AdminOfferListDto,
  FilteredParams<AdminOfferListDto, OffersFilter>,
  AdminOfferGetDto,
  AdminOfferPostPutDto
> {
  getAll(
    param: GetAllRequestParameters<FilteredParams<AdminOfferListDto, OffersFilter>>,
  ): Observable<ListPaginatedDTO<AdminOfferListDto>> {
    return from(
      listAdminOffersAsync({
        body: {
          pagination: convertPaginationParams(param.pagination),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          sort: mapSort(param.parameters?.sort) as any,
          searchText: param.parameters?.filter?.searchText,
          status: param.parameters?.filter?.status,
          language: param.parameters?.filter?.language,
          isHighlighted: param.parameters?.filter?.isHighlighted,
          categoryIds: param.parameters?.filter?.categoryIds ?? [],
        },
      }).then(resp => resp.data),
    );
  }

  getById(id: string): Observable<AdminOfferGetDto> {
    return from(findAdminOfferAsync({ path: { id } }).then(resp => resp.data));
  }

  create(data: AdminOfferPostPutDto): Observable<AdminOfferGetDto> {
    return from(createOfferAsync({ body: data }).then(resp => resp.data));
  }

  update(id: string, data: AdminOfferPostPutDto): Observable<void> {
    return from(updateOfferAsync({ path: { id }, body: data }).then(resp => resp.data));
  }

  getDtoId(dto: AdminOfferListDto): string;
  getDtoId(dto: AdminOfferListDto | null | undefined): string | null | undefined;
  getDtoId(dto: Partial<AdminOfferListDto> | null | undefined): string | null | undefined;
  getDtoId(
    dto: AdminOfferListDto | Partial<AdminOfferListDto> | null | undefined,
  ): string | null | undefined {
    return dto?.id ?? null;
  }

  toString(dto: AdminOfferListDto): string {
    return dto.title ?? UNKNOWN_ITEM;
  }

  delete(id: string): Observable<void> {
    return from(
      deleteAdminOfferAsync({ path: { id } }).then(() => {
        // type cast to void
      }),
    );
  }
}
